import React, { useEffect, useState, useCallback, useRef } from 'react';
import { adminAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Send, MessageCircle, ArrowLeft, Search } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminMessages() {
    const [conversations, setConversations] = useState([]);
    const [selected, setSelected] = useState(null);
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const [search, setSearch] = useState('');
    const messagesEndRef = useRef(null);
    const pollRef = useRef(null);

    const loadConversations = useCallback(async () => {
        try {
            const res = await adminAPI.getConversations();
            setConversations(res.data.conversations || []);
        } catch (err) {
            if (!loading) toast.error('Erreur chargement conversations');
        } finally {
            setLoading(false);
        }
    }, [loading]);

    const loadMessages = useCallback(async (supplierId) => {
        try {
            const res = await adminAPI.getMessages(supplierId);
            setMessages(res.data.messages || []);
            loadConversations();
        } catch (err) {
            toast.error('Erreur chargement messages');
        }
    }, [loadConversations]);

    useEffect(() => {
        loadConversations();
        pollRef.current = setInterval(() => {
            if (selected) loadMessages(selected.supplier_id);
            else loadConversations();
        }, 5000);
        return () => clearInterval(pollRef.current);
    }, [selected, loadConversations, loadMessages]);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const openConversation = (conv) => {
        setSelected(conv);
        loadMessages(conv.supplier_id);
    };

    const handleSend = async (e) => {
        e.preventDefault();
        if (!newMessage.trim() || !selected) return;
        setSending(true);
        try {
            await adminAPI.sendMessage(selected.supplier_id, { text: newMessage.trim() });
            setNewMessage('');
            loadMessages(selected.supplier_id);
        } catch (err) {
            toast.error('Erreur envoi');
        } finally {
            setSending(false);
        }
    };

    const filtered = conversations.filter(c =>
        !search || c.supplier_name.toLowerCase().includes(search.toLowerCase()) || c.supplier_code.toLowerCase().includes(search.toLowerCase())
    );

    if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-pulse text-muted-foreground">Chargement...</div></div>;

    return (
        <div className="h-[calc(100vh-140px)]" data-testid="admin-messages">
            <div className="mb-4">
                <h1 className="text-2xl font-bold text-jungle">Messages Fournisseurs</h1>
            </div>

            <div className="flex gap-4 h-[calc(100%-60px)]">
                {/* Conversations List */}
                <Card className={`rounded-xl w-80 flex-shrink-0 flex flex-col overflow-hidden ${selected ? 'hidden lg:flex' : 'flex'}`}>
                    <CardHeader className="py-3 px-3 border-b">
                        <div className="relative">
                            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                            <Input
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Rechercher..."
                                className="pl-8 h-9 rounded-lg"
                                data-testid="search-conversations"
                            />
                        </div>
                    </CardHeader>
                    <CardContent className="flex-1 overflow-y-auto p-0" data-testid="conversations-list">
                        {filtered.length === 0 ? (
                            <p className="text-center text-sm text-gray-400 py-8">Aucune conversation</p>
                        ) : filtered.map(conv => (
                            <button
                                key={conv.supplier_id}
                                onClick={() => openConversation(conv)}
                                className={`w-full text-left p-3 border-b hover:bg-gray-50 transition-colors ${selected?.supplier_id === conv.supplier_id ? 'bg-leaf/5 border-l-2 border-l-leaf' : ''}`}
                                data-testid={`conv-${conv.supplier_code}`}
                            >
                                <div className="flex items-center justify-between mb-0.5">
                                    <span className="font-semibold text-sm">{conv.supplier_name}</span>
                                    {conv.unread_count > 0 && (
                                        <span className="bg-red-500 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">
                                            {conv.unread_count}
                                        </span>
                                    )}
                                </div>
                                <div className="flex items-center justify-between">
                                    <p className="text-xs text-gray-500 font-mono">{conv.supplier_code}</p>
                                    {conv.last_message_at && (
                                        <p className="text-[10px] text-gray-400">
                                            {new Date(conv.last_message_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}
                                        </p>
                                    )}
                                </div>
                                {conv.last_message && (
                                    <p className="text-xs text-gray-400 truncate mt-1">{conv.last_message}</p>
                                )}
                            </button>
                        ))}
                    </CardContent>
                </Card>

                {/* Chat Area */}
                <Card className={`rounded-xl flex-1 flex flex-col overflow-hidden ${!selected ? 'hidden lg:flex' : 'flex'}`}>
                    {!selected ? (
                        <CardContent className="flex-1 flex items-center justify-center">
                            <div className="text-center text-gray-400">
                                <MessageCircle className="w-12 h-12 mx-auto mb-3 opacity-30" />
                                <p>Selectionnez une conversation</p>
                            </div>
                        </CardContent>
                    ) : (
                        <>
                            <CardHeader className="py-3 px-4 border-b flex-row items-center gap-3">
                                <Button variant="ghost" size="sm" onClick={() => setSelected(null)} className="lg:hidden p-1">
                                    <ArrowLeft className="w-4 h-4" />
                                </Button>
                                <div>
                                    <CardTitle className="text-sm font-semibold">{selected.supplier_name}</CardTitle>
                                    <p className="text-xs text-muted-foreground font-mono">{selected.supplier_code}</p>
                                </div>
                            </CardHeader>

                            <CardContent className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="chat-messages">
                                {messages.length === 0 ? (
                                    <div className="text-center py-12 text-gray-400">
                                        <p>Aucun message. Ecrivez le premier !</p>
                                    </div>
                                ) : messages.map(msg => {
                                    const isAdmin = msg.sender_role === 'admin';
                                    return (
                                        <div key={msg.id} className={`flex ${isAdmin ? 'justify-end' : 'justify-start'}`}>
                                            <div className={`max-w-[70%] rounded-2xl px-4 py-2.5 ${isAdmin ? 'bg-jungle text-sand rounded-br-md' : 'bg-gray-100 text-gray-800 rounded-bl-md'}`}
                                                data-testid={`msg-${msg.id}`}>
                                                {!isAdmin && <p className="text-[10px] font-semibold mb-0.5 opacity-70">{msg.sender_name}</p>}
                                                <p className="text-sm whitespace-pre-wrap">{msg.text}</p>
                                                <p className={`text-[10px] mt-1 ${isAdmin ? 'text-sand/60' : 'text-gray-400'}`}>
                                                    {new Date(msg.created_at).toLocaleString('fr-FR', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: 'short' })}
                                                </p>
                                            </div>
                                        </div>
                                    );
                                })}
                                <div ref={messagesEndRef} />
                            </CardContent>

                            <form onSubmit={handleSend} className="p-3 border-t flex gap-2 bg-white">
                                <Input
                                    value={newMessage}
                                    onChange={(e) => setNewMessage(e.target.value)}
                                    placeholder="Ecrivez votre message..."
                                    className="flex-1 rounded-xl"
                                    data-testid="admin-message-input"
                                />
                                <Button
                                    type="submit"
                                    disabled={sending || !newMessage.trim()}
                                    className="bg-jungle hover:bg-jungle/90 text-sand rounded-xl px-4"
                                    data-testid="admin-send-btn"
                                >
                                    <Send className="w-4 h-4" />
                                </Button>
                            </form>
                        </>
                    )}
                </Card>
            </div>
        </div>
    );
}
